import { $, $$ } from '../utils.js';
import { CONFIG, SELECTOR_CURSOR_PAIRS } from '../config.js';
import { heroIntersectionObserver, heroVisibilityManager } from '../utils/observerManagers.js';

export const CursorAnimation = {
  selectors: [],
  cursors: [],
  cursorData: [],
  animationId: null,
  isVisible: true,
  isInViewport: true,
  heroElement: null,
  eventListeners: [],
  
  init() {
    this.heroElement = $(CONFIG.selectors.heroSection);
    if (!this.heroElement) return;
    
    this.selectors = SELECTOR_CURSOR_PAIRS.map(config => 
      this.heroElement.querySelector(config.sel)
    );
    this.cursors = SELECTOR_CURSOR_PAIRS.map(config => $(config.cur));
  },
  
  runTapStagger() {
    SELECTOR_CURSOR_PAIRS.forEach((config, index) => {
      const element = this.selectors[index];
      if (!element) return;
      
      setTimeout(() => {
        element.style.color = config.colour;
        element.classList.add('tap');
        
        setTimeout(() => {
          element.classList.remove('tap');
          element.style.color = '';
        }, CONFIG.animation.tapDur);
      }, CONFIG.animation.tapDelay + index * CONFIG.animation.tapStagger);
    });
  },
  
  runCursorEntrance(callback) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !this.heroElement) {
      if (callback) callback();
      return;
    }
    
    const heroRect = this.heroElement.getBoundingClientRect();
    
    this.cursors.forEach((cursor, index) => {
      if (!cursor) return;
      
      const cursorRect = cursor.getBoundingClientRect();
      const cursorCenterX = cursorRect.left + cursorRect.width / 2;
      const cursorCenterY = cursorRect.top + cursorRect.height / 2;
      const heroCenterX = heroRect.left + heroRect.width / 2;
      const heroCenterY = heroRect.top + heroRect.height / 2;
      
      const deltaX = cursorCenterX - heroCenterX;
      const deltaY = cursorCenterY - heroCenterY;
      const distance = Math.hypot(deltaX, deltaY) || 1;
      
      const entranceX = (deltaX / distance) * CONFIG.animation.entrOffset;
      const entranceY = (deltaY / distance) * CONFIG.animation.entrOffset;
      
      cursor.style.setProperty('--entrX', entranceX + 'px');
      cursor.style.setProperty('--entrY', entranceY + 'px');
      cursor.classList.add('init');
      
      setTimeout(() => {
        requestAnimationFrame(() => {
          cursor.classList.add('enter');
          cursor.style.transform = 'translate(0px, 0px)';
          cursor.style.opacity = '1';
        });
      }, CONFIG.animation.tapDelay + index * CONFIG.animation.tapStagger);
    });
    
    const totalDelay = CONFIG.animation.tapDelay + 
      (SELECTOR_CURSOR_PAIRS.length - 1) * CONFIG.animation.tapStagger + 
      CONFIG.animation.entrDur + 50;
    
    setTimeout(callback, totalDelay);
  },
  
  enableMotion() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !this.cursors.length || !this.heroElement) {
      return;
    }
    
    // Initialize cursor data
    this.cursorData = this.cursors.map(cursor => {
      if (!cursor) return null;
      
      const rect = cursor.getBoundingClientRect();
      return {
        cur: cursor,
        cx: rect.left + rect.width / 2,
        cy: rect.top + rect.height / 2,
        phaseX: Math.random() * 2 * Math.PI,
        phaseY: Math.random() * 2 * Math.PI,
        dx: 0,
        dy: 0,
        bx: 0,
        by: 0
      };
    }).filter(Boolean);
    
    if (!this.cursorData.length) return;
    
    // Mouse tracking state
    this.mouseState = {
      isMouseOver: false,
      mouseX: 0,
      mouseY: 0
    };
    
    const handleMouseEnter = (e) => {
      this.mouseState.isMouseOver = true;
      this.mouseState.mouseX = e.clientX;
      this.mouseState.mouseY = e.clientY;
    };
    
    const handleMouseMove = (e) => {
      this.mouseState.mouseX = e.clientX;
      this.mouseState.mouseY = e.clientY;
    };
    
    const handleMouseLeave = () => {
      this.mouseState.isMouseOver = false;
    };
    
    // Add event listeners
    this.heroElement.addEventListener('mouseenter', handleMouseEnter, { passive: true });
    this.heroElement.addEventListener('mousemove', handleMouseMove, { passive: true });
    this.heroElement.addEventListener('mouseleave', handleMouseLeave, { passive: true });
    
    this.eventListeners.push(
      { element: this.heroElement, event: 'mouseenter', handler: handleMouseEnter },
      { element: this.heroElement, event: 'mousemove', handler: handleMouseMove },
      { element: this.heroElement, event: 'mouseleave', handler: handleMouseLeave }
    );
    
    this.setupVisibilityObserver(this.heroElement);
    this.startAnimationLoop();
  },
  
  setupVisibilityObserver(element) {
    heroVisibilityManager.register((isVisible) => {
      this.isVisible = isVisible && this.isInViewport;
      this.isVisible ? this.resumeAnimation() : this.pauseAnimation();
    }, 'cursor-animation');
    
    heroIntersectionObserver.observe(element, (entry) => {
      this.isInViewport = entry.isIntersecting;
      this.isVisible = this.isInViewport && !document.hidden;
      this.isVisible ? this.resumeAnimation() : this.pauseAnimation();
    }, 'cursor-animation');
  },
  
  pauseAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  },
  
  resumeAnimation() {
    if (!this.animationId && this.cursorData?.length && this.isVisible) {
      this.startAnimationLoop();
    }
  },
  
  startAnimationLoop() {
    this.pauseAnimation();
    
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      
      this.cursorData.forEach(data => {
        // Wave motion
        const waveX = Math.sin(data.phaseX + elapsed * CONFIG.animation.wavySpeed) * CONFIG.animation.wavyAmpl;
        const waveY = Math.sin(data.phaseY + elapsed * CONFIG.animation.wavySpeed) * CONFIG.animation.wavyAmpl;
        
        // Mouse influence
        if (this.mouseState && this.mouseState.isMouseOver) {
          data.bx += (Math.sign(this.mouseState.mouseX - data.cx) * CONFIG.animation.pointerBias - data.bx) * CONFIG.animation.biasLerp;
          data.by += (Math.sign(this.mouseState.mouseY - data.cy) * CONFIG.animation.pointerBias - data.by) * CONFIG.animation.biasLerp;
        } else {
          data.bx += -data.bx * CONFIG.animation.biasLerp;
          data.by += -data.by * CONFIG.animation.biasLerp;
        }
        
        data.dx = waveX + data.bx;
        data.dy = waveY + data.by;
        
        // Apply transform
        data.cur.style.transform = `translate(${data.dx}px, ${data.dy}px)`;
      });
      
      if (this.isVisible) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.animationId = null;
      }
    };
    
    this.animationId = requestAnimationFrame(animate);
  },
  
  destroy() {
    this.pauseAnimation();
    
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.eventListeners = [];
    
    heroVisibilityManager.unregister('cursor-animation');
    if (this.heroElement) {
      heroIntersectionObserver.unobserve(this.heroElement);
    }
    
    this.selectors = [];
    this.cursors = [];
    this.cursorData = [];
    this.heroElement = null;
    this.isVisible = true;
    this.isInViewport = true;
  }
};